import { Typography } from '@material-ui/core'
import {
  AcUnit as AcUnitIcon,
  AssignmentInd as AssignmentIndIcon,
  CheckCircle as CheckCircleIcon,
  Computer as ComputerIcon,
  DirectionsCar as DirectionsCarIcon,
  DirectionsSubway as DirectionsSubwayIcon,
  Fastfood as FastfoodIcon,
  FitnessCenter as FitnessCenterIcon,
  HotTub as HotTubIcon,
  LocalAirport as LocalAirportIcon,
  LocalLaundryService as LocalLaundryServiceIcon,
  Pool as PoolIcon,
  RoomService as RoomServiceIcon,
  Security as SecurityIcon,
  SpeakerPhone as SpeakerPhoneIcon,
  Storefront as StorefrontIcon,
  Wifi as WifiIcon
} from '@material-ui/icons'
import ApartmentIcon from '@material-ui/icons/Apartment'
import CakeIcon from '@material-ui/icons/Cake'
import GolfCourseIcon from '@material-ui/icons/GolfCourse'
import LocalCafeIcon from '@material-ui/icons/LocalCafe'
import LocalLibraryIcon from '@material-ui/icons/LocalLibrary'
import MenuBookIcon from '@material-ui/icons/MenuBook'

import useStyles from './facility.styles'

// --- Facility English names ---
export type FacilityName =
  | 'Coffee in the lobby'
  | 'Dry Clean Laundry and Ironing Service'
  | 'Internet'
  | 'Reception'
  | 'Newspaper'
  | 'Car Park'
  | 'Elevator Lift'
  | 'Swimming Pool'
  | 'Wake-up Calls'
  | 'Room Service'
  | 'Gym'
  | 'Sauna'
  | 'Wedding Services'
  | 'Towel change'
  | 'Cleaning Services'
  | 'Safety Deposit Box'
  | 'Coffee in the Lobby'
  | 'Lobby'
  | 'TV in the Lobby'
  | 'Conference Meeting Rooms'
  | 'Business Center'
  | 'Scanner'
  | 'Printer Facilities'
  | 'Photocopy Facilities'
  | 'Fax Facilities'
  | 'Steam Room'
  | 'Hot Tub Jacuzzi'
  | 'Coffee Shop Cafe'
  | 'Security'
  | 'Airport Shuttle'
  | 'Breakfast Buffet'
  | 'Extra cleaning'
  | 'Car Hire'
  | 'Concierge Services'
  | 'Extra linen'
  | 'Extra towel'
  | 'Shoe Polishing Service'
  | 'Luggage Storage'
  | 'Doctor on Call'
  | 'Ice Machine'
  | 'multilingual-staff'
  | 'Socket Adaptors'
  | 'Umbrella'
  | 'Security CCTV'
  | 'City Map'
  | 'Currency Exchange'
  | 'Emergency Maintenance'
  | 'Delivery Shopping Service'
  | 'Extra bed'
  | 'Porter'
  | 'ATM Machine'
  | 'Smoking Area'
  | 'Breakfast Room'
  | 'Restaurant on-site'
  | 'Translation Services'
  | 'Library'
  | 'Golf'

// --- English → Persian translations ---
const facilityTranslations: Record<FacilityName, string> = {
  'Coffee in the lobby': 'قهوه در لابی',
  'Dry Clean Laundry and Ironing Service': 'خشکشویی و اتوکشی',
  Internet: 'اینترنت',
  Reception: 'پذیرش',
  Newspaper: 'روزنامه',
  'Car Park': 'پارکینگ',
  'Elevator Lift': 'آسانسور',
  'Swimming Pool': 'استخر',
  'Wake-up Calls': 'تماس بیدارباش',
  'Room Service': 'خدمات اتاق',
  Gym: 'باشگاه',
  Sauna: 'سونا',
  'Wedding Services': 'خدمات عروسی',
  'Towel change': 'تعویض حوله',
  'Cleaning Services': 'خدمات نظافت',
  'Safety Deposit Box': 'صندوق امانات',
  'Coffee in the Lobby': 'قهوه در لابی',
  Lobby: 'لابی',
  'TV in the Lobby': 'تلویزیون در لابی',
  'Conference Meeting Rooms': 'سالن کنفرانس',
  'Business Center': 'مرکز تجاری',
  Scanner: 'اسکنر',
  'Printer Facilities': 'چاپگر',
  'Photocopy Facilities': 'کپی',
  'Fax Facilities': 'فکس',
  'Steam Room': 'اتاق بخار',
  'Hot Tub Jacuzzi': 'جکوزی',
  'Coffee Shop Cafe': 'کافه',
  Security: 'امنیت',
  'Airport Shuttle': 'ترانسفر فرودگاهی',
  'Breakfast Buffet': 'بوفه صبحانه',
  'Extra cleaning': 'نظافت اضافه',
  'Car Hire': 'اجاره خودرو',
  'Concierge Services': 'خدمات راهنما',
  'Extra linen': 'ملحفه اضافه',
  'Extra towel': 'حوله اضافه',
  'Shoe Polishing Service': 'واکس کفش',
  'Luggage Storage': 'انبار چمدان',
  'Doctor on Call': 'پزشک شبانه‌روزی',
  'Ice Machine': 'دستگاه یخ‌ساز',
  'multilingual-staff': 'کارکنان چندزبانه',
  'Socket Adaptors': 'تبدیل برق',
  Umbrella: 'چتر',
  'Security CCTV': 'دوربین مداربسته',
  'City Map': 'نقشه شهر',
  'Currency Exchange': 'صرافی',
  'Emergency Maintenance': 'تعمیرات اضطراری',
  'Delivery Shopping Service': 'خدمات خرید',
  'Extra bed': 'تخت اضافه',
  Porter: 'باربر',
  'ATM Machine': 'خودپرداز',
  'Smoking Area': 'محل سیگار',
  'Breakfast Room': 'سالن صبحانه',
  'Restaurant on-site': 'رستوران',
  'Translation Services': 'خدمات ترجمه',
  Library: 'کتابخانه',
  Golf: 'گلف'
}

// --- Persian → English map (auto reverse) ---
const faToEn: Record<string, FacilityName> = Object.entries(
  facilityTranslations
).reduce((acc, [en, fa]) => {
  acc[fa] = en as FacilityName
  return acc
}, {} as Record<string, FacilityName>)

// --- English names → icons ---
const facilityIcons: { [key in FacilityName]: JSX.Element } = {
  'Coffee in the lobby': <LocalCafeIcon />,
  'Dry Clean Laundry and Ironing Service': <LocalLaundryServiceIcon />,
  Internet: <WifiIcon />,
  Reception: <CheckCircleIcon />,
  Newspaper: <MenuBookIcon />,
  'Car Park': <DirectionsCarIcon />,
  'Elevator Lift': <DirectionsSubwayIcon />,
  'Swimming Pool': <PoolIcon />,
  'Wake-up Calls': <SpeakerPhoneIcon />,
  'Room Service': <RoomServiceIcon />,
  Gym: <FitnessCenterIcon />,
  Sauna: <HotTubIcon />,
  'Wedding Services': <CakeIcon />,
  'Towel change': <LocalLaundryServiceIcon />,
  'Cleaning Services': <LocalLaundryServiceIcon />,
  'Safety Deposit Box': <SecurityIcon />,
  'Coffee in the Lobby': <AcUnitIcon />,
  Lobby: <DirectionsSubwayIcon />,
  'TV in the Lobby': <LocalAirportIcon />,
  'Conference Meeting Rooms': <LocalAirportIcon />,
  'Business Center': <LocalAirportIcon />,
  Scanner: <ComputerIcon />,
  'Printer Facilities': <ComputerIcon />,
  'Photocopy Facilities': <ComputerIcon />,
  'Fax Facilities': <ComputerIcon />,
  'Steam Room': <HotTubIcon />,
  'Hot Tub Jacuzzi': <HotTubIcon />,
  'Coffee Shop Cafe': <FastfoodIcon />,
  Security: <SecurityIcon />,
  'Airport Shuttle': <LocalAirportIcon />,
  'Breakfast Buffet': <FastfoodIcon />,
  'Extra cleaning': <LocalLaundryServiceIcon />,
  'Car Hire': <DirectionsCarIcon />,
  'Concierge Services': <CheckCircleIcon />,
  'Extra linen': <LocalLaundryServiceIcon />,
  'Extra towel': <LocalLaundryServiceIcon />,
  'Shoe Polishing Service': <DirectionsCarIcon />,
  'Luggage Storage': <LocalAirportIcon />,
  'Doctor on Call': <CheckCircleIcon />,
  'Ice Machine': <FastfoodIcon />,
  'multilingual-staff': <CheckCircleIcon />,
  'Socket Adaptors': <ComputerIcon />,
  Umbrella: <LocalAirportIcon />,
  'Security CCTV': <SecurityIcon />,
  'City Map': <DirectionsSubwayIcon />,
  'Currency Exchange': <DirectionsCarIcon />,
  'Emergency Maintenance': <LocalLaundryServiceIcon />,
  'Delivery Shopping Service': <StorefrontIcon />,
  'Extra bed': <AssignmentIndIcon />,
  Porter: <CheckCircleIcon />,
  'ATM Machine': <DirectionsCarIcon />,
  'Smoking Area': <CheckCircleIcon />,
  'Breakfast Room': <FastfoodIcon />,
  'Restaurant on-site': <FastfoodIcon />,
  'Translation Services': <WifiIcon />,
  Library: <LocalLibraryIcon />,
  Golf: <GolfCourseIcon />
}

// --- Component ---
export function FacilityIcon({ name }: { name: string }) {
  const classes = useStyles()

  // normalize: if fa → convert to en
  const enName = (faToEn[name] as FacilityName) || (name as FacilityName)

  const icon = facilityIcons[enName] ?? <ApartmentIcon />

  return (
    <div className={classes.root}>
      <span className={classes.iconWrapper}>{icon}</span>
      <Typography variant="caption" className={classes.text}>
        {name}
      </Typography>
    </div>
  )
}
